import { FormEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ZodIssue } from 'zod'
import { AxiosError } from 'axios'
import { toast } from 'sonner'
import { changePasswordSchema } from '@/features/auth/auth-model'
import { AuthLayout } from '@/features/auth/auth-layout'
import { useChangePassword, useGetUserSession } from '@/shared/hooks/api/auth'
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Loader2 } from 'lucide-react'

function formatChangePasswordIssues(
  issues: ZodIssue[],
  t: (key: string) => string
): string {
  return issues
    .map((issue) => {
      const path0 = issue.path[0]
      if (path0 === 'oldPassword' && issue.code === 'too_small') {
        return t('changePassword.fields.oldPassword.errors.too_small')
      }
      if (path0 === 'oldPassword' && issue.code === 'too_big') {
        return t('changePassword.fields.oldPassword.errors.too_big')
      }
      if (path0 === 'newPassword' && issue.code === 'too_small') {
        return t('changePassword.fields.newPassword.errors.too_small')
      }
      if (path0 === 'newPassword' && issue.code === 'too_big') {
        return t('changePassword.fields.newPassword.errors.too_big')
      }
      if (path0 === 'newPasswordRepeat' && issue.code === 'too_small') {
        return t('changePassword.fields.newPasswordRepeat.errors.too_small')
      }
      if (path0 === 'newPasswordRepeat' && issue.code === 'too_big') {
        return t('changePassword.fields.newPasswordRepeat.errors.too_big')
      }
      if (issue.message === 'New passwords must match') {
        return t('changePassword.fields.newPasswordRepeat.errors.custom')
      }
      return issue.message
    })
    .join(' ')
}

export function ChangePasswordPage() {
  const { t } = useTranslation()
  const { refetch: fetchSession } = useGetUserSession()
  const [errors, setErrors] = useState<ZodIssue[]>([])
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordRepeat, setNewPasswordRepeat] = useState('')
  const { mutateAsync, isPending } = useChangePassword()

  const onSubmit = async (evt: FormEvent<HTMLFormElement>) => {
    evt.preventDefault()
    setErrors([])

    const validationResult = changePasswordSchema.safeParse({
      oldPassword: currentPassword,
      newPassword,
      newPasswordRepeat,
    })

    if (!validationResult.success) {
      setErrors(validationResult.error.issues)
    } else {
      try {
        await mutateAsync(validationResult.data)
        toast.success(t('notifications.saveSuccess'))
        await fetchSession()
      } catch (err) {
        if (err instanceof AxiosError && err.response?.status === 401) {
          toast.error(t('login.errors.unauthorized'))
        } else {
          toast.error(t('login.errors.general'))
        }
      }
    }
  }

  return (
    <AuthLayout>
      <Card className="w-full max-w-md gap-4">
        <CardHeader>
          <CardTitle>{t('changePassword.actions.submit')}</CardTitle>
          <CardDescription>{t('changePassword.description')}</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={onSubmit} className="space-y-4">
            {errors.length > 0 && (
              <Alert variant="destructive">
                <AlertDescription>
                  {formatChangePasswordIssues(errors, t)}
                </AlertDescription>
              </Alert>
            )}
            <div className="grid gap-2">
              <Label htmlFor="cp-old">{t('changePassword.fields.oldPassword.label')}</Label>
              <Input
                id="cp-old"
                type="password"
                value={currentPassword}
                onChange={(event) => setCurrentPassword(event.target.value)}
                autoComplete="current-password"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-new">{t('changePassword.fields.newPassword.label')}</Label>
              <Input
                id="cp-new"
                type="password"
                value={newPassword}
                onChange={(event) => setNewPassword(event.target.value)}
                autoComplete="new-password"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="cp-repeat">
                {t('changePassword.fields.newPasswordRepeat.label')}
              </Label>
              <Input
                id="cp-repeat"
                type="password"
                value={newPasswordRepeat}
                onChange={(event) => setNewPasswordRepeat(event.target.value)}
                autoComplete="new-password"
              />
            </div>
            <Button
              type="submit"
              className="w-full"
              disabled={
                currentPassword === '' ||
                newPassword === '' ||
                newPasswordRepeat === '' ||
                isPending
              }
            >
              {isPending ? <Loader2 className="animate-spin" /> : null}
              {t('changePassword.actions.submit')}
            </Button>
          </form>
        </CardContent>
      </Card>
    </AuthLayout>
  )
}
